// VGM (Video Game Music) parser — the chip-agnostic register-log format
// that is the universal container for Munos sound (docs/sound.md §2). A
// VGM file is a header (which chips at which clocks, loop point, sample
// counts) followed by a flat stream of commands: register writes, timed
// waits, and an end marker. We parse a *subset profile*: the chips our
// cores emulate (NES 2A03, GB DMG) plus the core write / wait / data-block
// / end commands. Unknown commands are skipped by their spec operand
// length so a real tracker export (Furnace, DefleMask) that carries
// commands for chips we don't synthesize still parses without desyncing.
//
// Pure: depends only on the ChipId type. The player (./vgm-player.ts)
// consumes the parsed command list; the synth (./vgm-synth.ts) binds it
// to a real core's APU.
export class VgmParseError extends Error {
    constructor(message) {
        super(message);
        this.name = "VgmParseError";
    }
}
// VGM base sample rate — all wait counts are in samples at this rate.
export const VGM_SAMPLE_RATE = 44100;
// Header field offsets (bytes).
const OFF_IDENT = 0x00;
const OFF_EOF = 0x04;
const OFF_VERSION = 0x08;
const OFF_TOTAL_SAMPLES = 0x18;
const OFF_LOOP_OFFSET = 0x1c;
const OFF_LOOP_SAMPLES = 0x20;
const OFF_DATA_OFFSET = 0x34;
const OFF_GB_DMG_CLOCK = 0x80;
const OFF_NES_APU_CLOCK = 0x84;
const IDENT = 0x206d6756; // "Vgm " little-endian
// Parse a VGM (or already-decompressed .vgz) byte blob. Throws
// VgmParseError on a bad magic or a truncated/desynced stream.
export function parseVgm(bytes) {
    if (bytes.length < 0x40) {
        throw new VgmParseError(`too short to be a VGM (${bytes.length} bytes)`);
    }
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const u32 = (off) => dv.getUint32(off, true);
    if (u32(OFF_IDENT) !== IDENT) {
        throw new VgmParseError("bad magic (not a 'Vgm ' file; gzip-compressed .vgz must be inflated first)");
    }
    const version = u32(OFF_VERSION);
    // Data start: 0x34 holds a *relative* offset (from 0x34). VGM < 1.50
    // predates that field and always starts data at 0x40.
    const dataOffsetField = u32(OFF_DATA_OFFSET);
    const dataStart = version < 0x150 || dataOffsetField === 0
        ? 0x40
        : 0x34 + dataOffsetField;
    // End of the command stream: 0x04 holds EOF relative to 0x04.
    const eofField = u32(OFF_EOF);
    const dataEnd = Math.min(eofField === 0 ? bytes.length : 0x04 + eofField, bytes.length);
    // Chip clocks live deep in the header; only read a field if the header
    // is actually that long (older/short headers stop before them).
    const chips = [];
    const readClock = (off) => off + 4 <= dataStart && off + 4 <= bytes.length ? u32(off) : 0;
    const nesClock = readClock(OFF_NES_APU_CLOCK) & 0x7fffffff; // bit 31 = FDS, ignore
    const gbClock = readClock(OFF_GB_DMG_CLOCK);
    if (nesClock !== 0)
        chips.push({ chip: "nes-2a03", clock: nesClock });
    if (gbClock !== 0)
        chips.push({ chip: "gb-dmg", clock: gbClock });
    const totalSamples = u32(OFF_TOTAL_SAMPLES);
    const loopOffsetField = u32(OFF_LOOP_OFFSET);
    const loopSamples = u32(OFF_LOOP_SAMPLES);
    const loopAbs = loopOffsetField === 0 ? -1 : 0x1c + loopOffsetField;
    const commands = [];
    const dataBlocks = [];
    let loopCommandIndex = null;
    let p = dataStart;
    // Record the loop target as a command index the first time we cross it.
    const noteLoop = (atByte) => {
        if (loopAbs >= 0 && loopCommandIndex === null && atByte >= loopAbs) {
            loopCommandIndex = commands.length;
        }
    };
    while (p < dataEnd) {
        noteLoop(p);
        const cmd = bytes[p++];
        // ----- waits -----
        if (cmd === 0x61) {
            if (p + 2 > bytes.length)
                throw new VgmParseError("truncated 0x61 wait");
            commands.push({ op: "wait", samples: bytes[p] | (bytes[p + 1] << 8) });
            p += 2;
            continue;
        }
        if (cmd === 0x62) {
            commands.push({ op: "wait", samples: 735 });
            continue;
        }
        if (cmd === 0x63) {
            commands.push({ op: "wait", samples: 882 });
            continue;
        }
        if (cmd >= 0x70 && cmd <= 0x7f) {
            commands.push({ op: "wait", samples: (cmd & 0x0f) + 1 });
            continue;
        }
        // ----- end of stream -----
        if (cmd === 0x66) {
            commands.push({ op: "end" });
            break;
        }
        // ----- register writes we synthesize -----
        if (cmd === 0xb4) { // NES APU: reg = $4000 + aa
            if (p + 2 > bytes.length)
                throw new VgmParseError("truncated 0xB4 NES write");
            commands.push({ op: "write", chip: "nes-2a03", reg: bytes[p], value: bytes[p + 1] });
            p += 2;
            continue;
        }
        if (cmd === 0xb3) { // GB DMG: reg = $FF10 + aa
            if (p + 2 > bytes.length)
                throw new VgmParseError("truncated 0xB3 GB write");
            commands.push({ op: "write", chip: "gb-dmg", reg: bytes[p], value: bytes[p + 1] });
            p += 2;
            continue;
        }
        // ----- data block (sample payload) -----
        if (cmd === 0x67) {
            // 0x67 0x66 tt ssssssss <data>
            if (p + 6 > bytes.length || bytes[p] !== 0x66) {
                throw new VgmParseError("malformed 0x67 data block");
            }
            const blockType = bytes[p + 1];
            const size = dv.getUint32(p + 2, true);
            const dataAt = p + 6;
            if (dataAt + size > bytes.length)
                throw new VgmParseError("data block overruns file");
            dataBlocks.push({ blockType, data: bytes.subarray(dataAt, dataAt + size) });
            p = dataAt + size;
            continue;
        }
        // ----- everything else: skip by spec operand length -----
        const skip = operandLength(cmd, bytes, p);
        if (skip < 0) {
            throw new VgmParseError(`unknown VGM command 0x${cmd.toString(16)} at byte ${p - 1}`);
        }
        p += skip;
    }
    return {
        version,
        chips,
        totalSamples,
        loopSamples,
        loopCommandIndex,
        commands,
        dataBlocks,
    };
}
// Operand byte count for a command we don't act on, so the parser can
// step over writes destined for chips outside our subset (FM, PSG, etc.)
// without desyncing. Returns -1 for a genuinely unknown byte. Ranges
// follow the VGM command spec; the chip-write ranges are the bulk.
function operandLength(cmd, bytes, p) {
    // Single reserved/operand byte.
    if (cmd >= 0x30 && cmd <= 0x3f)
        return 1;
    // Game Gear stereo / SN76489 — one operand.
    if (cmd === 0x4f || cmd === 0x50)
        return 1;
    // Two-operand reserved + classic 2-byte chip writes.
    if (cmd >= 0x40 && cmd <= 0x4e)
        return 2;
    if (cmd >= 0x51 && cmd <= 0x5f)
        return 2;
    // 0x80-0x8f: YM2612 port-0 DAC write + wait n (no operand byte).
    if (cmd >= 0x80 && cmd <= 0x8f)
        return 0;
    // DAC stream control block (0x90-0x95) — fixed sizes.
    if (cmd === 0x90)
        return 4;
    if (cmd === 0x91)
        return 4;
    if (cmd === 0x92)
        return 5;
    if (cmd === 0x93)
        return 10;
    if (cmd === 0x94)
        return 1;
    if (cmd === 0x95)
        return 4;
    // 0x68: PCM RAM write — 0x68 0x66 cc oo oo oo dd dd dd ss ss ss.
    if (cmd === 0x68)
        return 11;
    // Generic chip-write ranges by operand count.
    if (cmd >= 0xa0 && cmd <= 0xbf)
        return 2; // (0xb3/0xb4 handled above)
    if (cmd >= 0xc0 && cmd <= 0xdf)
        return 3;
    if (cmd >= 0xe0 && cmd <= 0xff)
        return 4;
    void bytes;
    void p;
    return -1;
}
//# sourceMappingURL=vgm.js.map